/**
 * Dialog for bulk publishing or unpublishing shifts in a date range.
 * Shows a count of affected shifts and requires confirmation before proceeding.
 * In publish mode it lists the employees who get a push with their draft counts, and the
 * already published shifts that changed since (#2038). With a group filter the action and
 * the counts stay inside that group (#2038 UX pass). A period that starts sooner than the
 * org's publication lead time gets a warning line; publishing stays possible (#2042).
 */

import { useMemo } from 'react';
import { useLingui } from '@lingui/react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Loader2, Send, Undo2 } from 'lucide-react';
import {
  useBulkPublish,
  useBulkUnpublish,
  useCountShiftsByStatus,
  usePublishPreview,
  useShifts,
} from '@/hooks/useShifts';
import { formatDateDisplay, formatDateRange } from '@/lib/format';
import { ShiftStatus } from '@/types/shifts';
import { publishEmployeeName, summarizePublish } from './publishSummary';
import { EMPTY_WRITE_SCOPE, scopeAllowsGroups, shiftGroupIds } from './shiftWriteScope';

export interface PublishWeekDialogProps {
  /** Whether the dialog is for publishing or unpublishing */
  mode: 'publish' | 'unpublish';
  /** Start date of the range (YYYY-MM-DD) */
  fromDate: string;
  /** End date of the range (YYYY-MM-DD) */
  toDate: string;
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when the dialog open state changes */
  onOpenChange: (open: boolean) => void;
  /** Optional callback on successful operation */
  onSuccess?: () => void;
  /** Optional group IDs to filter bulk publish scope */
  groupIds?: string[];
  /** Name of the filtered group, shown when `groupIds` holds one group. */
  groupName?: string | null;
  /** True while the planner's employee filter is set; the action ignores that filter. */
  employeeFiltered?: boolean;
  /** Opens the published range in the planner; offered as the action of the success toast */
  onShowRange?: (fromDate: string, toDate: string) => void;
  /**
   * A group lead's groups (#2043). The server publishes only shifts whose groups all lie in
   * them; the counts and lists follow, and one line names the groups.
   */
  leadGroupIds?: readonly string[];
  /** The names of `leadGroupIds`, comma separated. */
  leadGroupNames?: string;
}

/**
 * Alert dialog for confirming bulk publish or unpublish of shifts.
 * Fetches and displays the count of affected shifts before the user confirms.
 */
export function PublishWeekDialog({
  mode,
  fromDate,
  toDate,
  open,
  onOpenChange,
  onSuccess,
  groupIds,
  groupName,
  employeeFiltered = false,
  onShowRange,
  leadGroupIds,
  leadGroupNames = '',
}: PublishWeekDialogProps) {
  const { i18n } = useLingui();
  const bulkPublish = useBulkPublish({ onShowRange });
  const bulkUnpublish = useBulkUnpublish();
  const groupScoped = !!groupIds?.length;
  const singleGroupId = groupIds?.length === 1 ? groupIds[0] : undefined;
  const leadScoped = !!leadGroupIds?.length;
  // Counted from the range when a filter or a lead scope narrows the action.
  const countFromRange = groupScoped || leadScoped;

  // The range as the action sees it, for the employee and change lists. With a group
  // filter it is also the source of the counts: count-by-status knows no group.
  const { data: rangeData, isLoading: isRangeLoading } = useShifts(
    { fromDate, toDate, limit: 1000, ...(singleGroupId ? { groupId: singleGroupId } : {}) },
    { enabled: open && (mode === 'publish' || countFromRange) }
  );
  const rangeShifts = useMemo(() => {
    const all = rangeData?.shifts ?? [];
    if (!leadScoped) return all;
    const scope = { ...EMPTY_WRITE_SCOPE, groupIds: leadGroupIds ?? [] };
    return all.filter((s) => scopeAllowsGroups(scope, shiftGroupIds(s)));
  }, [rangeData?.shifts, leadScoped, leadGroupIds]);
  const summary = useMemo(() => summarizePublish(rangeShifts), [rangeShifts]);

  // Count shifts that will be affected
  const statusToCount = mode === 'publish' ? ShiftStatus.DRAFT : ShiftStatus.PUBLISHED;
  const { data: countData, isLoading: isOrgCountLoading } = useCountShiftsByStatus(
    fromDate,
    toDate,
    statusToCount,
    { enabled: open && !countFromRange }
  );

  // Publication lead time of the first day of the range (#2042).
  const { data: leadPreview } = usePublishPreview(fromDate, { enabled: open && mode === 'publish' });
  const leadWarning =
    mode === 'publish' && leadPreview?.late
      ? leadPreview.lead_days > 0
        ? i18n._({
            id: 'shifts.publish.leadLate',
            message:
              'The period starts in {days, plural, one {# day} other {# days}}; the target is {limit, plural, one {# day} other {# days}}.',
            values: { days: leadPreview.lead_days, limit: leadPreview.publish_lead_days },
          })
        : i18n._({
            id: 'shifts.publish.leadStarted',
            message:
              'The period starts today or has started; the target is {limit, plural, one {# day} other {# days}} ahead.',
            values: { limit: leadPreview.publish_lead_days },
          })
      : null;

  const isCountLoading = countFromRange ? isRangeLoading : isOrgCountLoading;
  const affectedCount = countFromRange
    ? rangeShifts.filter((s) => s.status === statusToCount).length
    : (countData?.count ?? 0);
  const isPending = bulkPublish.isPending || bulkUnpublish.isPending;

  const handleConfirm = async () => {
    const request = {
      from_date: fromDate,
      to_date: toDate,
      group_ids: groupIds?.length ? groupIds : undefined,
    };
    try {
      if (mode === 'publish') {
        await bulkPublish.mutateAsync(request);
      } else {
        await bulkUnpublish.mutateAsync(request);
      }
      onSuccess?.();
      onOpenChange(false);
    } catch {
      // Keep dialog open on error so user can see error toast and retry
    }
  };

  const title =
    mode === 'publish'
      ? i18n._({ id: 'shifts.publish.title', message: 'Publish Shifts' })
      : i18n._({ id: 'shifts.unpublish.title', message: 'Unpublish Shifts' });

  const scopedGroupName = groupScoped ? (groupName ?? '') : '';
  const description =
    mode === 'publish'
      ? groupScoped
        ? i18n._({
            id: 'shifts.publish.descriptionGroup',
            message: 'This will publish {count} draft shifts of {group} for the selected period.',
            values: { count: affectedCount, group: scopedGroupName },
          })
        : i18n._({
            id: 'shifts.publish.description',
            message:
              'This will publish {count} draft shifts for the selected period.',
            values: { count: affectedCount },
          })
      : groupScoped
        ? i18n._({
            id: 'shifts.unpublish.descriptionGroup',
            message: 'This will retract {count} published shifts of {group} back to draft.',
            values: { count: affectedCount, group: scopedGroupName },
          })
        : i18n._({
            id: 'shifts.unpublish.description',
            message:
              'This will retract {count} published shifts back to draft.',
            values: { count: affectedCount },
          });

  // What the action means for the employees, in one sentence (#2038 UX pass).
  const effectNote =
    mode === 'publish'
      ? i18n._({
          id: 'shifts.publish.effect',
          message: 'Employees see these shifts in the app as soon as you publish.',
        })
      : i18n._({
          id: 'shifts.unpublish.effect',
          message: 'Employees no longer see these shifts in the app. They are not notified.',
        });

  const confirmLabel =
    mode === 'publish'
      ? i18n._({ id: 'shifts.publish.confirm', message: 'Publish' })
      : i18n._({ id: 'shifts.unpublish.confirm', message: 'Unpublish' });

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              {isCountLoading ? (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>
                    {i18n._({
                      id: 'common.loading',
                      message: 'Loading...',
                    })}
                  </span>
                </div>
              ) : (
                <p>{description}</p>
              )}
              <p className="text-sm" data-testid="publish-effect">{effectNote}</p>

              {/* Publication lead time (#2042): a warning, publishing stays enabled; with
                  nothing to publish it says nothing */}
              {leadWarning && !isCountLoading && affectedCount > 0 && (
                <p
                  className="flex items-start gap-2 rounded-md border border-warning-on-surface/40 bg-warning-muted p-2 text-sm text-warning-on-surface"
                  data-testid="publish-lead-warning"
                >
                  <span
                    className="mt-0.5 shrink-0"
                    data-testid="publish-lead-icon"
                    title={i18n._({
                      id: 'shifts.publish.leadIconTitle',
                      message: 'Setting: Publish shifts ahead (days), under Settings → Shift planning',
                    })}
                  >
                    <AlertTriangle className="h-4 w-4" aria-hidden="true" />
                  </span>
                  <span>{leadWarning}</span>
                </p>
              )}

              {/* Date range summary */}
              <div className="rounded-md border p-3 bg-muted/50 text-sm text-muted-foreground">
                <span className="font-medium">
                  {i18n._({
                    id: 'shifts.publish.dateRange',
                    message: 'Period',
                  })}
                  :{' '}
                </span>
                {formatDateRange(fromDate, toDate, i18n.locale)}
              </div>

              {/* The scope the filters give the action (#2038 UX pass) */}
              {groupScoped && (
                <p className="text-xs text-muted-foreground" data-testid="publish-group-scope">
                  {i18n._({
                    id: 'shifts.publish.groupScope',
                    message: 'Only {group}, because the group filter is set. Clear the filter to include every group.',
                    values: { group: scopedGroupName },
                  })}
                </p>
              )}
              {leadScoped && (
                <p className="text-xs text-muted-foreground" data-testid="publish-lead-scope">
                  {mode === 'publish'
                    ? i18n._({
                        id: 'shifts.publish.leadScope',
                        message: 'Publishes only groups {groups}',
                        values: { groups: leadGroupNames },
                      })
                    : i18n._({
                        id: 'shifts.unpublish.leadScope',
                        message: 'Takes back only groups {groups}',
                        values: { groups: leadGroupNames },
                      })}
                </p>
              )}
              {employeeFiltered && (
                <p className="text-xs text-muted-foreground" data-testid="publish-employee-scope">
                  {i18n._({
                    id: 'shifts.publish.employeeScope',
                    message: 'The employee filter does not limit this: the shifts of every employee in the period are included.',
                  })}
                </p>
              )}

              {/* The employee and change lists load with the dialog (#2038 UX pass) */}
              {mode === 'publish' && isRangeLoading && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="publish-lists-loading">
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  {i18n._({ id: 'shifts.publish.listsLoading', message: 'Loading who will be notified…' })}
                </div>
              )}

              {/* Who gets a push (#2038) */}
              {mode === 'publish' && !isRangeLoading && summary.employees.length > 0 && (
                <div className="space-y-1" data-testid="publish-employees">
                  <p className="text-sm font-medium text-foreground">
                    {i18n._({
                      id: 'shifts.publish.employeesNotified',
                      message: '{count, plural, one {# employee gets a push} other {# employees each get a push}}',
                      values: { count: summary.employees.length },
                    })}
                  </p>
                  <ul className="max-h-40 overflow-y-auto text-sm">
                    {summary.employees.map((entry) => (
                      <li key={entry.employeeId} className="flex justify-between gap-4">
                        <span>{entry.name}</span>
                        <span className="text-muted-foreground">
                          {i18n._({
                            id: 'shifts.publish.draftCount',
                            message: '{count, plural, one {# draft} other {# drafts}}',
                            values: { count: entry.draftCount },
                          })}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Open shifts (#2040): no push, the groups find them under Open shifts */}
              {mode === 'publish' && !isRangeLoading && summary.openDrafts > 0 && (
                <p className="text-sm" data-testid="publish-open-shifts">
                  {i18n._({
                    id: 'shifts.publish.openShifts',
                    message:
                      '{count, plural, one {# open shift} other {# open shifts}}: the employees of the groups see them under Open shifts in the app and can ask for them. Nobody gets a push.',
                    values: { count: summary.openDrafts },
                  })}
                </p>
              )}

              {/* Already published, changed afterwards (#2038) */}
              {mode === 'publish' && !isRangeLoading && summary.changed.length > 0 && (
                <div className="space-y-1" data-testid="publish-changed">
                  <p className="text-sm font-medium text-foreground">
                    {i18n._({
                      id: 'shifts.publish.changedTitle',
                      message: 'Already published, changed since',
                    })}
                  </p>
                  <p className="text-xs">
                    {i18n._({
                      id: 'shifts.publish.changedHint',
                      message: 'These employees were notified when the shift changed. Publishing does not notify them again.',
                    })}
                  </p>
                  <ul className="max-h-32 overflow-y-auto text-sm">
                    {summary.changed.map((shift) => (
                      <li key={shift.id} className="flex justify-between gap-4">
                        <span>{publishEmployeeName(shift)}</span>
                        <span className="text-muted-foreground">
                          {formatDateDisplay(shift.shift_date, i18n.locale)} {shift.start_time}–{shift.end_time}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Warning for zero shifts */}
              {!isCountLoading && affectedCount === 0 && (
                <p className="text-sm text-muted-foreground italic">
                  {mode === 'publish'
                    ? i18n._({
                        id: 'shifts.publish.noDrafts',
                        message:
                          'There are no draft shifts in the selected period.',
                      })
                    : i18n._({
                        id: 'shifts.unpublish.noPublished',
                        message:
                          'There are no published shifts in the selected period.',
                      })}
                </p>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>
            {i18n._({ id: 'common.cancel', message: 'Cancel' })}
          </AlertDialogCancel>
          <Button
            onClick={handleConfirm}
            disabled={isPending || isCountLoading || affectedCount === 0}
            variant={mode === 'publish' ? 'default' : 'outline'}
          >
            {isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : mode === 'publish' ? (
              <Send className="mr-2 h-4 w-4" />
            ) : (
              <Undo2 className="mr-2 h-4 w-4" />
            )}
            {confirmLabel}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
